/**
 * CATATAN: file ini SENGAJA tanpa libphonenumber (dipakai form publik /f & /d).
 * Validasi ketat ada di formatPhone.lib.ts (dynamic import, chunk terpisah).
 */

/**
 * Normalize phone number for consistent DB storage (countryCode + digits format)
 */
/**
 * Format nomor ke antarabangsa TANPA menebak 62: hormati 60/62 yang sudah ada,
 * petakan lokal 01→60 (MY) / 0→countryCode. Idempoten — aman dipanggil berulang.
 */
export const formatWaIntl = (phone: string | null | undefined, country?: string | null): string => {
  if (!phone) return '';
  const cleaned = String(phone).replace(/\D/g, '');
  if (cleaned.startsWith('60') || cleaned.startsWith('62')) return cleaned;
  if (cleaned.startsWith('01')) return '60' + cleaned.slice(1);
  if (cleaned.startsWith('0')) return (country === 'MY' ? '60' : '62') + cleaned.slice(1);
  return cleaned;
};

/**
 * Tebak negara dari digit phone (untuk flag/label; BUKAN untuk routing).
 * '60...' (termasuk sisa korupsi '6260...' era bug 2026-10-04) → MY, selain itu ID.
 */
export const countryByPhone = (phone: string | null | undefined): 'ID' | 'MY' => {
  const d = String(phone || '').replace(/\D/g, '');
  const core = d.startsWith('6260') ? d.slice(2) : d;
  return core.startsWith('60') ? 'MY' : 'ID';
};

export const normalizePhoneForDb = (phone: string, countryCode: string = '62'): string => {
  if (!phone) return '';
  let cleaned = phone.replace(/\D/g, '');
  // Idempoten lintas-negara: sudah format antarabangsa (60/62) → jangan prepend lagi.
  // (Bug MY 2026-10-04: submitCustomer normalisasi 01→60, lalu submitPayload
  // normalisasi ulang default 62 → '6260123...' — untuk ID tidak terlihat karena no-op.)
  if (cleaned.startsWith('60') || cleaned.startsWith('62')) return cleaned;
  if (cleaned.startsWith('0')) cleaned = countryCode + cleaned.slice(1);
  if (!cleaned.startsWith(countryCode)) cleaned = countryCode + cleaned;
  return cleaned;
};

/**
 * Format phone number for display (e.g. 0812-3456-7890)
 */
export const formatPhoneDisplay = (phone: string, countryCode: string = '62'): string => {
  if (!phone) return '';
  let digits = phone.replace(/\D/g, '');
  if (digits.startsWith(countryCode)) digits = '0' + digits.slice(countryCode.length);
  if (digits.length === 0) return '';
  if (digits.length <= 6) return digits;
  if (digits.length <= 9) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  return `${digits.slice(0, 4)}-${digits.slice(4, 8)}-${digits.slice(8, 12)}`;
};

/**
 * Format phone with auto-masking while typing (live input formatting)
 */
export const formatPhoneInput = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 0) return '';
  if (digits.length <= 4) return digits;
  if (digits.length <= 8) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  return `${digits.slice(0, 4)}-${digits.slice(4, 8)}-${digits.slice(8, 12)}`;
};

/**
 * Validate phone number (min 4 digits, max 15 digits after normalization)
 */
export const isValidPhone = (phone: string, countryCode: string = '62'): boolean => {
  const digits = phone.replace(/\D/g, '');
  const normalized = digits.startsWith('0') ? countryCode + digits.slice(1) : digits.startsWith(countryCode) ? digits : countryCode + digits;
  return normalized.length >= 9 && normalized.length <= 16;
};
